import { useState, useRef } from 'react';
import { supabase } from '../supabase';
import { useToast } from '../context/ToastContext';
import { BookOpen } from 'lucide-react';

function EditarLivro({ livro, onVoltar, onSalvo }) {
  const { showToast } = useToast();
  const [titulo, setTitulo] = useState(livro.titulo || '');
  const [autor, setAutor] = useState(livro.autor || '');
  const [editora, setEditora] = useState(livro.editora || '');
  const [ano, setAno] = useState(livro.ano ? String(livro.ano) : '');
  const [descricao, setDescricao] = useState(livro.descricao || '');
  const [genero, setGenero] = useState(livro.genero || '');
  const [estrelas, setEstrelas] = useState(livro.estrelas || 0);
  const [comentario, setComentario] = useState(livro.comentario || '');
  const [especial, setEspecial] = useState(livro.especial || false);
  const [capaUrl, setCapaUrl] = useState(livro.capa_url || '');
  const [capaLocal, setCapaLocal] = useState(null);
  const [capaPreview, setCapaPreview] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const fileInputRef = useRef(null);

  function handleFotoChange(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    setCapaLocal(arquivo);
    const reader = new FileReader();
    reader.onload = ev => setCapaPreview(ev.target.result);
    reader.readAsDataURL(arquivo);
  }

  function removerCapa() {
    setCapaLocal(null);
    setCapaPreview('');
    setCapaUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSalvar() {
    if (!titulo || !autor) {
      setErro('Título e autor são obrigatórios.');
      return;
    }
    setErro('');
    setCarregando(true);

    let finalCapaUrl = capaUrl;

    if (capaLocal) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const ext = capaLocal.name.split('.').pop();
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { data, error } = await supabase.storage.from('capas').upload(path, capaLocal, { upsert: true });
        if (!error) {
          const { data: urlData } = supabase.storage.from('capas').getPublicUrl(data.path);
          finalCapaUrl = urlData.publicUrl;
        } else {
          showToast('Não foi possível fazer upload da capa.', 'info');
        }
      } catch {
        showToast('Não foi possível fazer upload da capa.', 'info');
      }
    }

    const { error } = await supabase.from('livros').update({
      titulo,
      autor,
      editora,
      ano: ano ? parseInt(ano) : null,
      descricao: descricao || null,
      estrelas,
      comentario,
      genero: genero || null,
      especial,
      disponivel: !especial,
      capa_url: finalCapaUrl || null,
    }).eq('id', livro.id);

    setCarregando(false);
    if (error) setErro('Erro ao salvar. Tente novamente.');
    else { showToast('Livro atualizado com sucesso!', 'success'); onSalvo(); }
  }

  const imagemCapa = capaPreview || capaUrl;
  const inputClass = 'w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-brand';

  return (
    <div className="bg-paper font-sans" style={{height:'100dvh', overflowY:'auto'}}>
      <div className="sticky top-0 z-10 flex items-center gap-3 px-[18px] pb-3"
        style={{paddingTop:'calc(env(safe-area-inset-top) + 44px)', background:'rgba(250,248,245,0.88)', backdropFilter:'blur(14px) saturate(160%)', WebkitBackdropFilter:'blur(14px) saturate(160%)', borderBottom:'1px solid #EBE8F0'}}>
        <button onClick={onVoltar}
          className="w-[38px] h-[38px] rounded-[12px] bg-white border border-[#EBE8F0] flex items-center justify-center flex-shrink-0 text-ink-700">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <h1 className="flex-1 text-[17px] font-extrabold tracking-tight text-ink-900 m-0">Editar livro</h1>
      </div>

      <div className="max-w-2xl mx-auto px-5 pb-10 pt-5 flex flex-col gap-4">

        {/* Capa */}
        <div className="flex gap-4">
          <div className="w-[92px] h-[136px] rounded-[10px] overflow-hidden flex-shrink-0 flex items-center justify-center"
            style={{background:'#E8E4F0', boxShadow:'3px 3px 12px rgba(33,28,43,0.14)'}}>
            {imagemCapa
              ? <img src={imagemCapa} alt="Capa" className="w-full h-full object-cover" />
              : <BookOpen size={32} color="#C4BCDA" strokeWidth={1} />}
          </div>
          <div className="flex-1 flex flex-col justify-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
              onChange={handleFotoChange} className="hidden" id="editar-foto-capa" />
            <label htmlFor="editar-foto-capa"
              className="flex flex-col items-center justify-center gap-1.5 rounded-[13px] cursor-pointer py-4 text-center"
              style={{border:'2px dashed #CFC5F7', background:'#F7F5FE', minHeight:72}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B3CE4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              <span className="text-[12px] font-bold" style={{color:'#5B3CE4'}}>
                {imagemCapa ? 'Trocar capa' : 'Adicionar capa'}
              </span>
            </label>
            {imagemCapa && (
              <button onClick={removerCapa}
                className="text-[11px] text-ink-400 hover:text-red-400 transition-colors text-center">
                Remover capa
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs text-ink-400 mb-1 block">Título *</label>
          <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="text-xs text-ink-400 mb-1 block">Autor *</label>
          <input type="text" value={autor} onChange={e => setAutor(e.target.value)} className={inputClass} />
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-ink-400 mb-1 block">Editora</label>
            <input type="text" value={editora} onChange={e => setEditora(e.target.value)} className={inputClass} />
          </div>
          <div className="w-24">
            <label className="text-xs text-ink-400 mb-1 block">Ano</label>
            <input type="number" value={ano} onChange={e => setAno(e.target.value)} className={inputClass} />
          </div>
        </div>

        <div>
          <label className="text-xs text-ink-400 mb-1 block">Gênero <span className="text-ink-300">(opcional)</span></label>
          <select value={genero} onChange={e => setGenero(e.target.value)} className={inputClass}
            style={{appearance:'none', WebkitAppearance:'none'}}>
            <option value="">Selecionar gênero</option>
            {[
              { id: 'ficcao', label: 'Ficção' }, { id: 'romance', label: 'Romance' },
              { id: 'fantasia', label: 'Fantasia' }, { id: 'sci-fi', label: 'Ficção Científica' },
              { id: 'terror', label: 'Terror' }, { id: 'misterio', label: 'Mistério' },
              { id: 'thriller', label: 'Thriller' }, { id: 'autoajuda', label: 'Autoajuda' },
              { id: 'negocios', label: 'Negócios' }, { id: 'biografia', label: 'Biografia' },
              { id: 'historia', label: 'História' }, { id: 'ciencia', label: 'Ciência' },
              { id: 'filosofia', label: 'Filosofia' }, { id: 'poesia', label: 'Poesia' },
              { id: 'hq', label: 'HQ / Quadrinhos' }, { id: 'manga', label: 'Mangá' },
              { id: 'jovem-adulto', label: 'Jovem Adulto' }, { id: 'infantil', label: 'Infantil' },
              { id: 'religioso', label: 'Religioso/Espiritual' }, { id: 'saude', label: 'Saúde & Bem-estar' },
              { id: 'tecnologia', label: 'Tecnologia' }, { id: 'culinaria', label: 'Culinária' },
              { id: 'esportes', label: 'Esportes' }, { id: 'cronicas', label: 'Crônicas' },
              { id: 'outro', label: 'Outro' },
            ].map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs text-ink-400 mb-1 block">Descrição <span className="text-ink-300">(opcional)</span></label>
          <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
            rows={4} className={`${inputClass} resize-none`} placeholder="Sinopse ou descrição do livro..." />
        </div>

        <div>
          <label className="text-xs text-ink-400 mb-2 block">Minha avaliação</label>
          <div className="flex gap-2">
            {[1,2,3,4,5].map(s => (
              <button key={s} onClick={() => setEstrelas(s === estrelas ? 0 : s)}
                className={`text-2xl transition-colors ${s <= estrelas ? 'text-amber-400' : 'text-ink-300'}`}>★</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-ink-400 mb-1 block">Meu comentário <span className="text-ink-300">(opcional)</span></label>
          <textarea value={comentario} onChange={e => setComentario(e.target.value)}
            rows={3} className={`${inputClass} resize-none`} />
        </div>

        {/* Livro especial */}
        <button type="button" onClick={() => setEspecial(v => !v)}
          className="w-full flex items-center justify-between px-4 py-[14px] rounded-[14px] border-0 cursor-pointer text-left"
          style={{background: especial ? '#FFF8E1' : '#F5F4F7'}}>
          <div>
            <p className="text-[14px] font-bold m-0" style={{color: especial ? '#B87A00' : '#6B6478'}}>
              Livro especial
            </p>
            <p className="text-[12px] m-0 mt-0.5" style={{color: especial ? '#D4920A' : '#9A93A4'}}>
              {especial ? 'Privado — só aparece na sua estante, nunca publicamente' : 'Livro normal, visível e disponível para empréstimo'}
            </p>
          </div>
          <div className="flex-shrink-0 w-11 h-6 rounded-full transition-colors relative"
            style={{background: especial ? '#E6A800' : '#D1CDD8'}}>
            <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform"
              style={{transform: especial ? 'translateX(22px)' : 'translateX(2px)'}} />
          </div>
        </button>

        {erro && <p className="text-xs text-red-400 bg-red-50 rounded-xl px-4 py-3">{erro}</p>}

        <button onClick={handleSalvar} disabled={carregando}
          className="w-full bg-brand text-white py-3 rounded-xl text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50">
          {carregando ? 'Salvando...' : 'Salvar alterações'}
        </button>

      </div>
    </div>
  );
}

export default EditarLivro;
